// The BFF derives every refusal code from the payment method itself:
// `${method.toUpperCase().replace(/-/g, '_')}_REJECTED`, in
// checkout-bff-api/src/domain/exceptions/payment-rejected.exception.ts.
//
// Listing those codes by hand is what broke: only credit card was ever mapped, so a
// Google Pay, Pix or boleto refusal fell through to DEFAULT_ERROR and rendered as a red
// "unexpected error" instead of the amber "transaction not authorized". Deriving them from
// the method codes keeps the two sides from drifting again.
//
// The list mirrors the BFF's PaymentMethods enum (src/domain/configs/payment-methods.ts).
// Watch out for `bankslip`: it has no hyphen, so its code is BANKSLIP_REJECTED — not
// BANK_SLIP_REJECTED.
const REJECTABLE_PAYMENT_METHODS = [
  'credit-card',
  'credit-card+credit-card',
  'credit-card-in-person',
  'multiple-payment',
  'apple-pay',
  'google-pay',
  'pix',
  'pix-in-person',
  'bankslip',
  'nu-pay',
  'cheque',
  'cash',
  'm-pesa',
];

const rejectionCodes = REJECTABLE_PAYMENT_METHODS.map(
  method => `${method.toUpperCase().replace(/-/g, '_')}_REJECTED`,
);

const rejectionEntries = message =>
  Object.fromEntries(rejectionCodes.map(code => [code, message]));

const errorsByLang = {
  BR: {
    ...rejectionEntries(
      'Entre em contato com a sua operadora para solucionar ou tente novamente.',
    ),
    RATE_LIMIT_CARD:
      'O sistema de auditoria verificou que você fez várias tentativas com o mesmo cartão e para evitar possíveis fraudes esta transação foi bloqueada.',
    TOO_MANY_REQUESTS:
      'Ops, parece que houve várias tentativas. Espere o desbloqueio para tentar novamente.',
    CREDIT_CARD_INVALID: 'Revise o número do cartão digitado.',
    ACCOUNT_ALREADY_EXISTS:
      'Já existe uma conta ativa com os dados informados. Em caso de dúvidas, entre em contato com a Central de Ajuda dentro do seu perfil.',
    PHONE_NUMBER_INVALID: 'Revise o número do telefone digitado.',
    SOURCE_PHONE_NUMBER_INVALID: 'Este número já está cadastrado no sistema.',
    CARD_REJECTED:
      'Entre em contato com a sua operadora para solucionar ou tente novamente.',
    CREDIT_CARD_REJECTED:
      'Entre em contato com a sua operadora para solucionar ou tente novamente.',
    'CREDIT_CARD+CREDIT_CARD_REJECTED':
      'Entre em contato com a sua operadora para solucionar ou tente novamente.',
    SUBSCRIPTION_NOT_FOUND: 'Assinatura Wiseu Up Online não encontrada.',
    NOT_ELIGIBLE:
      'O e-mail informado não é elegível para essa ação promocional.',
    DEFAULT_ERROR: 'Ocorreu um erro inesperado. Tente novamente mais tarde.',
    PLAN_SOLD_OUT:
      'Este pagamento não foi concluído e nenhum valor foi cobrado. Sua compra anterior continua confirmada.',
    // FIN-8268: o BFF recusa a criação do boleto nos últimos dias de venda da
    // oferta (trava do boleto). Boleto só existe no BR, então não há versão PE.
    BOLETO_CUTOFF_PASSED:
      'O boleto não está mais disponível nos últimos dias de venda desta oferta. Selecione outro método de pagamento.',
    ERROR_PAYMENT:
      'Transação não autorizada pelo seu banco. Entre em contato com a sua operadora para solucionar ou tente novamente com outro cartão.',
    INVOICE_NOT_FOUND: 'Fatura não encontrada',
    INVOICE_OVERDUE:
      'Esta fatura está vencida e não pode ser paga desta forma. Entre em contato com a central de atendimento para regularizar.',
    INVOICE_ALREADY_PAID: 'Esta fatura já está paga.',
    INVOICE_CANNOT_BE_PAID: 'Esta fatura não pode ser paga por este meio.',
    NO_PURCHASE: 'Compra do lead não encontrada.',
    APPLICATION_ERROR: 'Já existe uma conta criada com este e-mail.',
    PAY_ERROR:
      'Transação não autorizada pelo seu banco. Entre em contato com a sua operadora para solucionar ou tente novamente com outro cartão.',
    CANCELLED_BY_INSTITUTION:
      'Verifique se o CPF informado possui conta no Nubank ou se ocorreu algum erro ao realizar o pagamento pelo aplicativo.',
    TICKET_SOLD_OUT:
      'Não se preocupe, você pode aproveitar para acompanhar o evento online.',
    PLAN_NOT_FOUND:
      'Oferta não encontrada ou inativa. Em caso de dúvidas consulte o responsável pela oferta',
    MULTIPLE_ATTEMPTS:
      'Pagamento em processamento. Verifique seu e-mail ou aguarde antes de tentar novamente.',
    VALIDATION_ERROR:
      'Identificamos um problema com as informações enviadas. Por favor, revise os dados e tente novamente',
    PAYMENT_PROFILE_IS_INVALID: 'Por favor, cadastre um cartão válido',
    ENROLLMENT_NOT_FOUND: 'Verifique os dados e tente novamente.',
    PRODUCT_ALREADY_PAID:
      'Material didático já adquirido, em caso de dúvidas entre em contato com a central de atendimento',
    RECURRING_SALE_NOT_ALLOWED:
      'Este link não está disponível para a sua unidade. Em caso de dúvidas, entre em contato com a central de atendimento.',
    MULTIPLE_PAYMENT_REJECTED:
      'Entre em contato com a sua operadora para solucionar ou tente novamente.',
    // FIN-7199 — análise de crédito (boleto parcelado no MD). Copy aprovada.
    CREDIT_ANALYSIS_NOT_ELIGIBLE:
      'O boleto recorrente não está disponível.<br />Clique no link a seguir e conclua sua compra com as formas de pagamento disponíveis.',
    CREDIT_ANALYSIS_SCHOOL_NOT_ENABLED:
      'O boleto recorrente não está disponível.<br />Clique no link a seguir e conclua sua compra com as formas de pagamento disponíveis.',
    CREDIT_ANALYSIS_UNAVAILABLE:
      'Consulta indisponível no momento, tente novamente mais tarde.',
  },
  PE: {
    ...rejectionEntries(
      'Comunícate con tu entidad bancaria para solucionarlo o inténtalo nuevamente.',
    ),
    RATE_LIMIT_CARD:
      'El sistema de auditoría detectó varios intentos con la misma tarjeta y, para evitar posibles fraudes, esta transacción fue bloqueada.',
    TOO_MANY_REQUESTS:
      'Ups, parece que hubo varios intentos. Espera a que se desbloquee para volver a intentarlo.',
    CREDIT_CARD_INVALID: 'Revisa el número de tarjeta ingresado.',
    ACCOUNT_ALREADY_EXISTS:
      'Ya existe una cuenta activa con los datos ingresados. Si tienes dudas, comunícate con la Central de Ayuda dentro de tu perfil.',
    PHONE_NUMBER_INVALID: 'Revisa el número de teléfono ingresado.',
    SOURCE_PHONE_NUMBER_INVALID:
      'Este número ya está registrado en el sistema.',
    CARD_REJECTED:
      'Comunícate con tu entidad bancaria para solucionarlo o inténtalo nuevamente.',
    CREDIT_CARD_REJECTED:
      'Comunícate con tu entidad bancaria para solucionarlo o inténtalo nuevamente.',
    'CREDIT_CARD+CREDIT_CARD_REJECTED':
      'Comunícate con tu entidad bancaria para solucionarlo o inténtalo nuevamente.',
    SUBSCRIPTION_NOT_FOUND: 'Suscripción de Wise Up Online no encontrada.',
    NOT_ELIGIBLE:
      'El correo electrónico ingresado no es elegible para esta acción promocional.',
    DEFAULT_ERROR:
      'Ocurrió un error inesperado. Inténtalo nuevamente más tarde.',
    ERROR_PAYMENT:
      'Transacción no autorizada por tu banco. Comunícate con tu entidad bancaria para solucionarlo o inténtalo nuevamente con otra tarjeta.',
    INVOICE_NOT_FOUND: 'Factura no encontrada',
    INVOICE_OVERDUE:
      'Esta factura está vencida y no puede pagarse de esta forma. Comunícate con la central de atención para regularizarla.',
    INVOICE_ALREADY_PAID: 'Esta factura ya está pagada.',
    INVOICE_CANNOT_BE_PAID: 'Esta factura no puede pagarse por este medio.',
    NO_PURCHASE: 'Compra del lead no encontrada.',
    APPLICATION_ERROR:
      'Ya existe una cuenta creada con este correo electrónico.',
    PAY_ERROR:
      'Transacción no autorizada por tu banco. Comunícate con tu entidad bancaria para solucionarlo o inténtalo nuevamente con otra tarjeta.',
    CANCELLED_BY_INSTITUTION:
      'Verifica si el documento ingresado tiene cuenta en la entidad correspondiente o si ocurrió algún error al realizar el pago desde la aplicación.',
    TICKET_SOLD_OUT:
      'No te preocupes, puedes aprovechar para seguir el evento en línea.',
    PLAN_NOT_FOUND:
      'Oferta no encontrada o inactiva. Si tienes dudas, consulta al responsable de la oferta.',
    MULTIPLE_ATTEMPTS:
      'Pago en procesamiento. Revisa tu correo electrónico o espera antes de volver a intentarlo.',
    VALIDATION_ERROR:
      'Identificamos un problema con la información enviada. Por favor, revisa los datos e inténtalo nuevamente.',
    PAYMENT_PROFILE_IS_INVALID: 'Por favor, registra una tarjeta válida.',
    ENROLLMENT_NOT_FOUND: 'Verifica los datos e inténtalo nuevamente.',
    PRODUCT_ALREADY_PAID:
      'Material didáctico ya adquirido. Si tienes dudas, comunícate con la central de atención.',
    RECURRING_SALE_NOT_ALLOWED:
      'Este enlace no está disponible para tu sede. Si tienes dudas, comunícate con la central de atención.',
    MULTIPLE_PAYMENT_REJECTED:
      'Comunícate con tu entidad bancaria para solucionarlo o inténtalo nuevamente.',
    // FIN-7199 — análisis de crédito (boleto en cuotas para MD). Copy de
    // CREDIT_ANALYSIS_NOT_ELIGIBLE/CREDIT_ANALYSIS_SCHOOL_NOT_ENABLED es
    // traducción da versão BR aprovada; ainda não confirmada com stakeholders PE.
    CREDIT_ANALYSIS_NOT_ELIGIBLE:
      'El boleto recurrente no está disponible.<br />Haz clic en el siguiente enlace y completa tu compra con los métodos de pago disponibles.',
    CREDIT_ANALYSIS_SCHOOL_NOT_ENABLED:
      'El boleto recurrente no está disponible.<br />Haz clic en el siguiente enlace y completa tu compra con los métodos de pago disponibles.',
    CREDIT_ANALYSIS_UNAVAILABLE:
      'Consulta no disponible en este momento, inténtalo nuevamente más tarde.',
  },
};

const errorsTitle = {
  BR: {
    ...rejectionEntries('Transação não autorizada'),
    RATE_LIMIT_CARD: 'Muitas tentativas!',
    TOO_MANY_REQUESTS: 'Muitas tentativas',
    CREDIT_CARD_INVALID: 'Número de cartão inválido',
    DEFAULT_ERROR: 'Mensagem de erro',
    SOURCE_PHONE_NUMBER_INVALID: 'Número de telefone inválido',
    PHONE_NUMBER_INVALID: 'Número de telefone inválido',
    TICKET_SOLD_OUT: 'Os ingressos presenciais esgotaram',
    PLAN_SOLD_OUT: 'As vagas desta oferta esgotaram',
    BOLETO_CUTOFF_PASSED: 'Boleto indisponível',
    CARD_REJECTED: 'Transação não autorizada',
    CREDIT_CARD_REJECTED: 'Transação não autorizada',
    'CREDIT_CARD+CREDIT_CARD_REJECTED': 'Transação não autorizada',
    PAYMENT_PROFILE_IS_INVALID: 'Cartão inválido',
    ENROLLMENT_NOT_FOUND: 'Aluno não encontrado.',
    RECURRING_SALE_NOT_ALLOWED: 'Link indisponível',
    MULTIPLE_PAYMENT_REJECTED: 'Transação não autorizada',
    INVOICE_OVERDUE: 'Fatura vencida',
    INVOICE_ALREADY_PAID: 'Fatura já paga',
    INVOICE_CANNOT_BE_PAID: 'Fatura indisponível para pagamento',
    CREDIT_ANALYSIS_NOT_ELIGIBLE: 'Vamos de outra maneira?',
    CREDIT_ANALYSIS_SCHOOL_NOT_ENABLED: 'Vamos de outra maneira?',
    CREDIT_ANALYSIS_UNAVAILABLE: 'Consulta indisponível',
  },
  PE: {
    ...rejectionEntries('Transacción no autorizada'),
    RATE_LIMIT_CARD: '¡Demasiados intentos!',
    TOO_MANY_REQUESTS: 'Demasiados intentos',
    CREDIT_CARD_INVALID: 'Número de tarjeta inválido',
    DEFAULT_ERROR: 'Mensaje de error',
    SOURCE_PHONE_NUMBER_INVALID: 'Número de teléfono inválido',
    PHONE_NUMBER_INVALID: 'Número de teléfono inválido',
    TICKET_SOLD_OUT: 'Las entradas presenciales se agotaron',
    CARD_REJECTED: 'Transacción no autorizada',
    CREDIT_CARD_REJECTED: 'Transacción no autorizada',
    'CREDIT_CARD+CREDIT_CARD_REJECTED': 'Transacción no autorizada',
    PAYMENT_PROFILE_IS_INVALID: 'Tarjeta inválida',
    ENROLLMENT_NOT_FOUND: 'Alumno no encontrado.',
    RECURRING_SALE_NOT_ALLOWED: 'Enlace no disponible',
    MULTIPLE_PAYMENT_REJECTED: 'Transacción no autorizada',
    INVOICE_OVERDUE: 'Factura vencida',
    INVOICE_ALREADY_PAID: 'Factura ya pagada',
    INVOICE_CANNOT_BE_PAID: 'Factura no disponible para pago',
    CREDIT_ANALYSIS_NOT_ELIGIBLE: '¿Vamos de otra manera?',
    CREDIT_ANALYSIS_SCHOOL_NOT_ENABLED: '¿Vamos de otra manera?',
    CREDIT_ANALYSIS_UNAVAILABLE: 'Consulta no disponible',
  },
};

// `fallbackMessage` is the `message` the BFF already sends next to the code. Preferring it
// over DEFAULT_ERROR means a code we have not mapped yet still shows the real reason
// instead of "an unexpected error occurred" — the failure mode this catalogue keeps hitting
// whenever a new payment method ships. Optional, so existing call sites keep working.
export const getErrorMessage = (
  operation = 'BR',
  code = 'DEFAULT_ERROR',
  fallbackMessage = '',
) => {
  return (
    errorsByLang[operation][code] ||
    fallbackMessage ||
    errorsByLang[operation].DEFAULT_ERROR
  );
};

export const getErrorTitle = (operation = 'BR', code = 'DEFAULT_ERROR') => {
  return errorsTitle[operation][code] || errorsTitle[operation].DEFAULT_ERROR;
};

const alertCodes = [
  // A refusal is the customer's card saying no, not a system failure: it belongs in the
  // amber alert, not the red error. Missing codes here is what painted a Google Pay
  // refusal red.
  ...rejectionCodes,
  'CARD_REJECTED',
  'MULTIPLE_ATTEMPTS',
  'VALIDATION_ERROR',
  'PAYMENT_PROFILE_IS_INVALID',
  'PRODUCT_ALREADY_PAID',
  'MULTIPLE_PAYMENT_REJECTED',
  'CREDIT_CARD_INVALID',
  'INVOICE_OVERDUE',
  'INVOICE_ALREADY_PAID',
  'INVOICE_CANNOT_BE_PAID',
  // FIN-8268: recusa que o aluno resolve trocando o método, não falha do sistema.
  'BOLETO_CUTOFF_PASSED',
  'CREDIT_ANALYSIS_NOT_ELIGIBLE',
  'CREDIT_ANALYSIS_SCHOOL_NOT_ENABLED',
];

export const getFeedbackType = (code = 'DEFAULT_ERROR') => {
  if (alertCodes.includes(code)) {
    return 'alert';
  }

  return 'error';
};
